import { Button, Col, DatePicker, Modal, Row, Table, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import type { InventorySummaryRow } from '../../contexts/ApiContext';
import type { StockInventoryItem } from '../../domain/metals';
import StockInventoryGrid from './StockInventoryGrid';

const { Title } = Typography;

export interface StockInvContentProps {
    stockInvDates: string[];
    stockInvSelectedRowKeys: React.Key[];
    setStockInvSelectedRowKeys: (keys: React.Key[]) => void;
    stockInvItems: Record<string, StockInventoryItem[]>;
    handleStockInvExpand: (expanded: boolean, record: { date: string }) => void;
    setStockInvSelectedDate: (date: string) => void;
    loadStockInventoryData: (date: string) => Promise<void>;
    handleMeltInventoryDates: () => Promise<void>;
    openMergeModal: () => Promise<void>;
    stockInvSelectedDate: string | null;
    stockInvSummary: Record<string, { date: string; grouped: InventorySummaryRow[] }>;
    mergeModalVisible: boolean;
    setMergeModalVisible: (visible: boolean) => void;
    mergeDate: Dayjs | null;
    setMergeDate: (date: Dayjs | null) => void;
    mergeMinDate: Dayjs | null;
    handleMergeConfirm: () => Promise<void>;
    t: (key: string) => string;
}

const StockInvContent: React.FC<StockInvContentProps> = ({
    stockInvDates,
    stockInvSelectedRowKeys,
    setStockInvSelectedRowKeys,
    stockInvItems,
    handleStockInvExpand,
    setStockInvSelectedDate,
    loadStockInventoryData,
    handleMeltInventoryDates,
    openMergeModal,
    stockInvSelectedDate,
    stockInvSummary,
    mergeModalVisible,
    setMergeModalVisible,
    mergeDate,
    setMergeDate,
    mergeMinDate,
    handleMergeConfirm,
    t,
}) => (
    <div>
        <Row gutter={16}>
            <Col span={12}>
                <Table
                    rowKey="date"
                    dataSource={stockInvDates.map(d => ({ date: d }))}
                    columns={[
                        {
                            title: t('metals.inventoryDate'),
                            dataIndex: 'date',
                            render: (d: string) => dayjs(d).format('YYYY-MM-DD'),
                        },
                    ]}
                    rowSelection={{
                        selectedRowKeys: stockInvSelectedRowKeys,
                        onChange: keys => setStockInvSelectedRowKeys(keys),
                    }}
                    expandable={{
                        expandedRowRender: record => (
                            <StockInventoryGrid
                                data={stockInvItems[record.date] ?? []}
                                loading={!stockInvItems[record.date]}
                                t={t}
                            />
                        ),
                        onExpand: handleStockInvExpand,
                    }}
                    pagination={false}
                    onRow={record => ({
                        onClick: () => {
                            setStockInvSelectedDate(record.date);
                            void loadStockInventoryData(record.date);
                        },
                    })}
                />
                <Button
                    type="primary"
                    style={{ marginTop: 16 }}
                    onClick={handleMeltInventoryDates}
                    disabled={!stockInvSelectedRowKeys.length}
                >
                    {t('metals.meltSelected')}
                </Button>
                <Button
                    style={{ marginTop: 16, marginLeft: 8 }}
                    onClick={openMergeModal}
                    disabled={!stockInvSelectedRowKeys.length}
                >
                    {t('metals.mergeSelected')}
                </Button>
            </Col>
            <Col span={12}>
                {stockInvSelectedDate && stockInvSummary[stockInvSelectedDate] && (
                    <div>
                        <Title level={4}>{t('metals.summary')}</Title>
                        <ul>
                            {stockInvSummary[stockInvSelectedDate].grouped.map(
                                (g: InventorySummaryRow) => (
                                    <li key={g.metal_type}>
                                        {g.metal_type}: {Number(g.totalQty).toFixed(2)} GR, PMP
                                        {Number(g.pmp).toFixed(2)} LEI, {t('metals.content999')}
                                        {Number(g.qty999).toFixed(2)} GR, {t('metals.valueRon')}
                                        {Number(g.totalLei).toFixed(2)} RON
                                    </li>
                                ),
                            )}
                        </ul>
                    </div>
                )}
            </Col>
        </Row>
        <Modal
            open={mergeModalVisible}
            onOk={handleMergeConfirm}
            onCancel={() => setMergeModalVisible(false)}
            okButtonProps={{ disabled: !mergeDate }}
            okText={t('metals.mergeSelected')}
        >
            <DatePicker
                value={mergeDate}
                onChange={setMergeDate}
                disabledDate={current => !!mergeMinDate && current.isBefore(mergeMinDate, 'day')}
                style={{ width: '100%' }}
            />
        </Modal>
    </div>
);

export default StockInvContent;
